import ComponentManager from "../../../componentFramework";
import Tooltip, {TooltipOptions} from "../../Tooltip";
import deepCopy from "../../../scripts/deepCopy";
import deepFreeze from "../../../scripts/deepFreeze";
import eventListenerOption from "../../../scripts/eventListenerOption";

export interface MessageTooltipOptions extends TooltipOptions {
	messageElement?: HTMLElement
}

/**
 * This turns the message into a tooltip that follows the user's mouse while being limited to the edges of the field the
 * message is for. It is also always aligned to point at the bottom of the field.
 * Instances are constructed by Message.ts (not by ComponentManager)
 */
export default class MessageTooltip extends Tooltip<MessageTooltipOptions> {
	public static componentStyleClass = "__inaccessible_tooltip";

	private static readonly tooltipContainerStyleClass = "messageTooltip";
	private static readonly visibleStyleClass = "isVisible";
	private static readonly focusStyleClass = "isFocused";

	private displayElement: HTMLElement | undefined;
	private clonedMessageElement: HTMLElement | undefined;

	public static readonly messageTooltipDefaultOptions: MessageTooltipOptions = deepFreeze(deepCopy([
		Tooltip.defaultOptions,
		{
			pointerOffset: 0,
			hideOnClick: false
		}
	]));
	private isDestroyed = false;
	private isFocusIn = false;

	/**
	 * @param element - Can be either the field element or the parent to the field (e.g. fieldArea).
	 * @param options
	 */
	constructor(element: HTMLElement, options: Partial<MessageTooltipOptions> & {messageElement: HTMLElement}) {
		super(
			element,
			MessageTooltip.messageTooltipDefaultOptions,
			options
		);

		const elem = this.getElement();
		const elemManaged = this.eventListenerManager.manage(elem);
		elemManaged.addEventListener("focusin", () => {
			if (!this.isMouseOver()) {
				// If the mouse is not over the area, update the display position
				this.updateDisplayPositionToElementPositionRelativeTo();
			}

			this.getDisplayElement()?.classList.add(MessageTooltip.focusStyleClass);
			this.isFocusIn = true;
		});
		elemManaged.addEventListener("focusout", () => {
			this.getDisplayElement()?.classList.remove(MessageTooltip.focusStyleClass);
			this.isFocusIn = false;
		});

		this.eventListenerManager.manage(window)
			.addEventListener("scroll", (ev: Event) => {
				const elem = this.getElement();
				if ((elem.ownerDocument.defaultView !== null && !(ev.target instanceof elem.ownerDocument.defaultView.Node)) || !elem.contains(ev.target as Node)) {
					// Was a scroll event from something other than this component.
					if (this.isFocusIn) {
						this.updateDisplayPositionToElementPositionRelativeTo();
					} else {
						this.hide();
					}
				}
			}, eventListenerOption.passiveCapture);
	}

	@ComponentManager.on("removed")
	public destroy() {
		this.isDestroyed = true;
		this.eventListenerManager.removeListeners();
		if (this.displayElement !== undefined) {
			this.displayElement.remove();
			this.displayElement = undefined;
		}
		this.clonedMessageElement = undefined;
		super.componentRemoved();
	}

	private updateDisplayPositionToElementPositionRelativeTo() {
		const boundingRect = this.getElementToPositionRelativeTo().getBoundingClientRect();
		this.updateDisplayPosition(
			boundingRect.left,
			(boundingRect.top + boundingRect.bottom) / 2
		);
	}

	protected determineCoordinatesForDisplayElement(eventX: number, eventY: number): {x: number, y: number} {
		const coordinates = {
			x: eventX,
			y: eventY
		};
		const displayElem = this.getDisplayElement();
		const elem = this.getElement();
		const docElem = elem.ownerDocument.documentElement;

		const boundingRect = this.getElementToPositionRelativeTo().getBoundingClientRect();

		const scrollY = window.scrollY || window.pageYOffset;
		coordinates.y = boundingRect.bottom + scrollY + 8; // The extra added is due to the arrow coming off of the message's container.

		const scrollX = window.scrollX || window.pageXOffset;
		coordinates.x = Math.max(boundingRect.left + scrollX, coordinates.x);
		coordinates.x = Math.min(boundingRect.right + scrollX, coordinates.x);
		coordinates.x -= 16; // This is to offset due to the arrow in the message tooltip display.

		if (displayElem !== undefined) {
			const options = this.getOptions();
			const amountOverRightEdge = (coordinates.x + displayElem.offsetWidth + options.viewGutter) - docElem.clientWidth;
			if (amountOverRightEdge > 0) {
				coordinates.x -= amountOverRightEdge;
			}

			displayElem.style.maxWidth = (docElem.clientWidth - (coordinates.x - scrollX) - options.viewGutter) + "px";
		}

		return coordinates;
	}

	private getElementToPositionRelativeTo(): Element {
		const element = this.getElement();
		if ((element as any).name !== undefined) {
			return element;
		}
		return element.querySelector(".ui-inputfield") ?? element.querySelector("[name]") ?? element; // .ui-inputfield is for Primefaces compatibility
	}

	protected getClonedMessageElement(): HTMLElement {
		if (this.clonedMessageElement === undefined) {
			const originalMessageElement = this.getOptions().messageElement;
			if (originalMessageElement !== undefined && originalMessageElement !== null) {
				this.clonedMessageElement = originalMessageElement.cloneNode(true) as HTMLElement;
				this.clonedMessageElement.removeAttribute("id");
				const element = this.getElement();
				ComponentManager.getInstance(element.ownerDocument).ignoreElement(this.clonedMessageElement, true, true);
			} else {
				throw `Missing required "messageElement" property on options which is passed during construction.`
			}
		}
		return this.clonedMessageElement;
	}
	protected getDisplayElement(): HTMLElement | undefined {
		if (!this.isDestroyed && this.displayElement === undefined) {
			const element = this.getElement();
			if (element === undefined) {
				return; // This instance probably had its destroy() called.
			}

			// Get messages element
			const msg = this.getClonedMessageElement();

			// Create a container element
			const doc = element.ownerDocument;
			const displayElement = doc.createElement("div");
			ComponentManager.getInstance(doc).ignoreElement(displayElement, true, true);
			displayElement.classList.add(MessageTooltip.tooltipContainerStyleClass);
			displayElement.appendChild(msg);
			doc.body.appendChild(displayElement);

			this.displayElement = displayElement;
		}
		return this.displayElement;
	}

	public show() {
		this.getDisplayElement()?.classList.add(MessageTooltip.visibleStyleClass);
	}

	public hide() { // Overriding and calling super
		this.getDisplayElement()?.classList.remove(MessageTooltip.visibleStyleClass);
		// Note: We are not removing the focus style class though. So if the field still has focus the message will continue to display.
	}
}
